import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, Select, IconButton, TextField, Switch } from '@mui/material';

// ============================================
// Error Animation
// ============================================
export const errorPulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`;

export const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError' && prop !== 'fullWidth',
})(({ hasError, fullWidth }) => ({
  display: 'inline-flex',
  borderRadius: '4px',
  ...(fullWidth && {
    flex: 1,
    '& > *': {
      width: '100%',
    },
  }),
  ...(hasError && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc2626 !important',
      borderWidth: '2px !important',
    },
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

// ============================================
// Container & Layout
// ============================================

export const PageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '8px',
  width: '100%',
});

export const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '24px',
  borderBottom: 'none',
});

export const HeaderTitle = styled(Typography)({
  fontSize: '18px',
  marginLeft: '8px',
  fontWeight: 550,
});

export const HeaderLink = styled('a')({
  fontSize: 14,
  fontWeight: 500,
  color: '#0F7F85',
  textDecoration: 'none',
});

export const Section = styled(Box)({
  padding: '0 24px',
  marginBottom: '32px',
});

export const SectionTitle = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  color: '#111',
  marginBottom: '20px',
});

export const SectionBox = styled(Box)({
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  padding: '20px 24px',
  marginTop: '16px',
});

export const SectionBoxHeader = styled(Box)({
  marginBottom: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
});

// ============================================
// Form Layout
// ============================================

export const FormRow = styled(Box)({
  display: 'flex',
  width: '100%',
  minHeight: 44,
  marginBottom: '16px',
  gap: '48px',
});

export const FormGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1 1 0',
  minWidth: 0,
  '& > .MuiTextField-root, & > .MuiFormControl-root, & > .MuiAutocomplete-root': {
    flex: 1,
  },
});

export const FormLabel = styled(Typography)({
  fontSize: 13,
  color: '#111',
  width: 56,
  minWidth: 56,
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

export const FormLabelSmall = styled(Typography)({
  fontSize: 13,
  fontWeight: 500,
  color: '#262626',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

export const FormSubLabel = styled(Typography)({
  fontSize: 14,
  fontWeight: 500,
  color: '#262626',
  marginBottom: '8px',
});

export const HintText = styled(Typography)({
  fontSize: 14,
  color: '#a6a6a6',
  fontWeight: 500,
});

export const UnitText = styled(Typography)({
  fontSize: 13,
  color: '#111',
  flexShrink: 0,
});

// ============================================
// Radio & Checkbox
// ============================================

export const RadioWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
});

export const RadioCircle = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: checked ? '2px solid #0F7F85' : '1px solid #d9d9d9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fff',
}));

export const RadioDot = styled(Box)({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: '#0F7F85',
});

export const CheckboxWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: 20,
  height: 20,
  border: '1.5px solid',
  borderColor: checked ? '#0F7F85' : '#d9d9d9',
  borderRadius: '2px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: checked ? '#0F7F85' : '#fff',
}));

// ============================================
// Day Selection
// ============================================

export const DayButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  width: 70,
  height: 44,
  border: '1px solid',
  borderColor: selected ? '#0F7F85' : '#d9d9d9',
  borderRadius: '4px',
  background: selected ? '#ECF7F8' : '#fff',
  fontSize: 13,
  fontWeight: 500,
  color: selected ? '#262626' : '#a6a6a6',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  flexShrink: 0,
  '&:hover': {
    borderColor: '#0F7F85',
  },
}));

// ============================================
// Time Selection
// ============================================

export const TimeSelect = styled(Select)({
  minWidth: 72,
  height: 44,
  flexShrink: 0,
});

export const TimeSeparator = styled(Typography)({
  fontSize: 16,
  fontWeight: 500,
  color: '#262626',
  width: 12,
  textAlign: 'center',
});

// ============================================
// Product Card
// ============================================

export const ProductCard = styled(Box)({
  width: 240,
  height: 88,
  border: '1px solid #0F7F85',
  borderRadius: '8px',
  padding: '16px',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

export const ProductBadge = styled('span')({
  background: '#6FB6B3',
  color: '#fff',
  fontSize: 12,
  padding: '4px 8px',
  borderRadius: '99px',
  fontWeight: 500,
});

export const ProductName = styled(Typography)({
  fontSize: 14,
  color: '#111',
});

export const ProductPrice = styled(Typography)({
  fontSize: 14,
  color: '#666',
});

// ============================================
// Buttons
// ============================================

export const PrimaryButton = styled(Button)({
  minWidth: 100,
  height: 40,
  border: 'none',
  borderRadius: '8px',
  background: '#0F7F85',
  color: '#fff',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  padding: '0 24px',
  '&:hover': {
    background: '#0a6a70',
  },
  '&:disabled': {
    background: '#ccc',
  },
});

export const SecondaryButton = styled(Button)({
  minWidth: 100,
  height: 40,
  border: '1px solid #0F7F85',
  borderRadius: '8px',
  background: '#fff',
  color: '#0F7F85',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  padding: '0 24px',
  '&:hover': {
    background: '#f5f5f5',
  },
});

export const CancelButton = styled(Button)({
  minWidth: 100,
  height: 40,
  border: 'none',
  borderRadius: '8px',
  background: '#E07979',
  color: '#fff',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  padding: '0 24px',
  '&:hover': {
    background: '#c96666',
  },
});

export const UploadButton = styled(Button)({
  minWidth: 113,
  height: 38,
  background: '#0F7F85',
  color: '#fff',
  fontSize: 14,
  borderRadius: '4px',
  '&:hover': {
    background: '#0a6a70',
  },
});

// ============================================
// Image Upload
// ============================================

export const ImageUploadContainer = styled(Box)({
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  padding: '24px',
});

export const ImagePreviewContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  marginBottom: '16px',
});

export const ImagePreviewWrapper = styled(Box)({
  position: 'relative',
  width: 160,
  height: 120,
});

export const ImagePreview = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '8px',
});

export const ImageRemoveButton = styled(IconButton)({
  position: 'absolute',
  top: 4,
  right: 4,
  background: 'rgba(0,0,0,0.5)',
  color: '#fff',
  width: 24,
  height: 24,
  '&:hover': {
    background: 'rgba(0,0,0,0.7)',
  },
});

export const ImageUploadBox = styled(Box)({
  width: 100,
  height: 100,
  border: '1px dashed #ddd',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#0F7F85',
  },
});

export const ImageUploadFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const ImageCountText = styled(Typography)({
  fontSize: 13,
  color: '#666',
});

export const ImageHintText = styled(Typography)({
  fontSize: 12,
  color: '#999',
  marginTop: '8px',
});

// ============================================
// Curriculum
// ============================================

export const CurriculumContainer = styled(Box)({
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  overflow: 'hidden',
});

export const CurriculumItem = styled(Box)({
  display: 'flex',
  gap: '24px',
  padding: '24px',
  background: '#fff',
  borderBottom: '1px solid #e5e5e5',
  '&:last-child': {
    borderBottom: 'none',
  },
});

export const CurriculumLeftColumn = styled(Box)({
  flex: 1,
});

export const CurriculumRightColumn = styled(Box)({
  flex: 1,
});

export const CurriculumTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
});

export const CurriculumInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: 44,
  },
});

export const CurriculumTextArea = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    minHeight: 120,
    alignItems: 'flex-start',
  },
});

export const CurriculumImageContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginTop: '8px',
});

export const CurriculumImageWrapper = styled(Box)({
  position: 'relative',
  width: 80,
  height: 80,
});

export const CurriculumImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '8px',
});

export const CurriculumImageRemoveButton = styled(IconButton)({
  position: 'absolute',
  top: 2,
  right: 2,
  background: 'rgba(0,0,0,0.5)',
  color: '#fff',
  width: 20,
  height: 20,
  '&:hover': {
    background: 'rgba(0,0,0,0.7)',
  },
});

export const CurriculumAddRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  background: '#fff',
});

export const CurriculumAddButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  color: '#666',
  '&:hover': {
    color: '#0F7F85',
  },
});

export const CurriculumCountText = styled(Typography)({
  fontSize: 13,
  color: '#999',
});

export const CurriculumInfoText = styled(Typography)({
  fontSize: 12,
  color: '#999',
  marginTop: '8px',
});

export const RemoveIconWrapper = styled('span')({
  marginLeft: 'auto',
  color: '#F87171',
  cursor: 'pointer',
});

// ============================================
// Class Detail (Editor)
// ============================================

export const ClassDetailWrapper = styled(Box)({
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  overflow: 'hidden',
});

// ============================================
// AI Toggle
// ============================================

export const AIToggleBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#ECF7F8',
  borderRadius: '8px',
  padding: '12px 16px',
  margin: '0 24px 24px',
});

export const AIToggleLabel = styled(Typography)({
  fontSize: 14,
  fontWeight: 500,
  color: '#0F7F85',
});

export const AIToggleLabelWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const AIToggleIconWrapper = styled(Box)({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0F7F85',
});

// ============================================
// Bottom Actions
// ============================================

export const BottomActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '24px',
  marginTop: '32px',
});

// ============================================
// Step01 추가 스타일
// ============================================

export const FormRowHalf = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '48px',
  width: '100%',
  minHeight: 44,
  marginBottom: '16px',
});

export const FormGroupFlex = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  flexWrap: 'nowrap',
});

export const StopSettingContainer = styled(Box)({
  display: 'flex',
  gap: '48px',
  marginBottom: '20px',
});

export const StopSettingLeft = styled(Box)({
  flex: 1,
});

export const StopSettingRight = styled(Box)({
  flex: 1,
});

export const StopInputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
});

export const StopInputRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flex: 1,
});

export const StopCheckboxGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
});

export const DayButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
});

export const TimeSettingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const TimeInputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const UsageLimitContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const ReservationCancelRow = styled(Box)({
  display: 'flex',
  gap: '16px',
  width: '100%',
});

export const ReservationBox = styled(Box)({
  display: 'flex',
  flex: 1,
  minWidth: 0,
});

export const ProductSelect = styled(Select)({
  width: '100%',
  maxWidth: 400,
  height: 44,
});

export const ProductCardContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
  flexWrap: 'wrap',
  marginTop: '16px',
});

export const ProductCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const ProductCardBadgeWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const CloseIconWrapper = styled('span')({
  color: '#6FB6B3',
  cursor: 'pointer',
});

export const PriceInputRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
  gap: '8px',
});

export const DiscountSettingRow = styled(Box)({
  display: 'flex',
  gap: '24px',
  alignItems: 'flex-start',
});

export const DiscountColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

export const SalePeriodRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: 16,
  gap: '48px',
});

export const SalePeriodGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const SalePeriodDateGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '380px',
  '& .MuiFormControl-root': {
    flex: 1,
  },
});

export const UsagePeriodGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiTextField-root': {
    flex: 'none',
  },
  '& > div': {
    flex: 'none',
  },
});

// ============================================
// Misc
// ============================================

export const BackButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const HeaderBackLink = styled(Box)({
  textDecoration: 'none',
  color: '#3a3a3a',
  display: 'flex',
  alignItems: 'center',
});

export const InfoText = styled(Typography)({
  fontSize: 12,
  color: '#999',
});

export const CountText = styled(Typography)({
  fontSize: 13,
  color: '#666',
  marginBottom: '8px',
});

// ============================================
// Edit Mode (Detail View) Styles
// ============================================

export const EditModeHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '0 24px',
});

export const StatusToggleWrapper = styled(Box)({
  display: 'flex',
  marginTop: '25px',
  marginBottom: '25px',
});

export const StatusToggleLabel = styled(Typography)({
  width: '70px',
  color: '#000',
});

export const PassInfoWrapper = styled(Box)({
  marginLeft: '40px',
  width: '100%',
});

export const PassInfoHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const PassInfoTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
});

export const StatusBadge = styled(Button)({
  width: 'auto',
  marginLeft: '8px',
  padding: '0',
});

export const CopyButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  flexShrink: 0,
  height: '36px',
  padding: '0 16px',
  marginLeft: 'auto',
  borderRadius: '8px',
  background: '#F1F4F6',
  color: '#707070',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#E4E9ED',
    boxShadow: 'none',
  },
});

export const WishlistWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const WishlistIcon = styled('img')({
  width: '16px',
  height: '17px',
});

export const WishlistText = styled(Typography)({
  marginLeft: '5px',
  fontSize: '12px',
});

export const Divider = styled('hr')({
  border: '1px solid #e1e1e1',
});

// ============================================
// Modal Styles
// ============================================

export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '420px',
  maxWidth: 'calc(100vw - 32px)',
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  border: 'none',
  outline: 'none',
  padding: '32px 28px 24px',
  boxShadow: '0 12px 32px rgba(20, 20, 20, 0.16)',
});

export const ModalCloseIcon = styled(Box)({
  position: 'absolute',
  top: '14px',
  right: '14px',
  width: '22px',
  height: '22px',
  cursor: 'pointer',
  color: '#A6A6A6',
  '&:hover': {
    color: '#404040',
  },
});

export const ModalIconBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: '#F3F9FA',
  marginBottom: '16px',
});

export const ModalContent = styled(Box)({
  width: '100%',
});

export const ModalTitle = styled(Typography)({
  fontSize: '17px',
  fontWeight: 700,
  lineHeight: 1.45,
  color: '#262626',
  textAlign: 'center',
  wordBreak: 'keep-all',
});

export const ModalDescription = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  width: '100%',
  marginTop: '10px',
});

export const ModalDescText = styled(Typography)({
  fontSize: '13px',
  lineHeight: 1.6,
  color: '#737373',
  textAlign: 'center',
  wordBreak: 'keep-all',
});

export const ModalDescTextSub = styled(Typography)({
  fontSize: '13px',
  lineHeight: 1.6,
  color: '#737373',
  textAlign: 'center',
  wordBreak: 'keep-all',
});

export const ModalButtonWrapper = styled(Box)({
  display: 'flex',
  gap: '8px',
  width: '100%',
  marginTop: '24px',
});

export const ModalCancelButton = styled(Button)({
  flex: 1,
  height: '44px',
  background: '#F2F2F2',
  color: '#404040',
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    background: '#D9D9D9',
    boxShadow: 'none',
  },
});

export const ModalConfirmButton = styled(Button)({
  flex: 1,
  height: '44px',
  background: '#4AB3BC',
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    background: '#3E9FA7',
    boxShadow: 'none',
  },
});

// ============================================
// Subscription Block Modal (미승인 지점 안내)
// ============================================

export const SubBlockModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '360px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  border: 'none',
  padding: '32px 28px 28px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  outline: 'none',
});

export const SubBlockCloseButton = styled(Box)({
  position: 'absolute',
  top: '16px',
  right: '16px',
  cursor: 'pointer',
  color: '#a6a6a6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  transition: 'color 0.15s ease',
  '&:hover': {
    color: '#404040',
  },
});

export const SubBlockIconWrapper = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  background: '#EDF8F9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
  color: '#4ab3bc',
});

export const SubBlockTitle = styled(Typography)({
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '18px',
  fontWeight: 700,
  color: '#141414',
  textAlign: 'center',
  marginBottom: '12px',
  lineHeight: 1.4,
});

export const SubBlockBody = styled(Box)({
  textAlign: 'center',
  marginBottom: '28px',
});

export const SubBlockBodyText = styled(Typography)({
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#737373',
  textAlign: 'center',
  lineHeight: 1.6,
});

export const SubBlockEmphasis = styled('span')({
  fontWeight: 600,
  color: '#4ab3bc',
});

export const SubBlockConfirmButton = styled(Button)({
  width: '100%',
  height: '48px',
  background: '#4ab3bc',
  color: '#fff',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '8px',
  fontFamily: 'Pretendard, sans-serif',
  letterSpacing: '-0.01em',
  '&:hover': {
    background: '#3d9a9f',
  },
});

// Delete Button
export const DeleteButton = styled(Button)({
  borderRadius: '12px',
  paddingLeft: '35px',
  paddingRight: '35px',
  height: '36px',
  background: '#f7f7f7',
  color: 'red',
  border: 'none',
  '&:hover': {
    border: 'none',
    background: '#ef0c23',
    color: '#fff',
  },
});

// Bottom Actions with Delete
export const BottomActionsWithDelete = styled(Box)({
  margin: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  maxWidth: '1080px',
});

export const BottomActionsRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
});

// ============================================
// New Card Layout (Figma Design Match)
// ============================================

export const Step1CardsContainer = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-start',
  padding: '0 24px',
});

export const Step1Card = styled(Box)(({ width }) => ({
  background: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: width || 'auto',
  alignSelf: 'stretch',
}));

export const InfoCardTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#4ab3bc',
});

export const InfoCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

export const FieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

export const FieldLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#141414',
});

export const FieldHint = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#a6a6a6',
});

export const FieldRow = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  width: '100%',
});

export const InputWithUnit = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
});

export const UnitLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

// RadioGroup Row
export const RadioRow = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'center',
  padding: '4px 0',
});

export const RadioItem = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  cursor: 'pointer',
});

// Day Buttons (New Design)
export const DayButtonsRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  width: '100%',
});

export const DayButtonNew = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  flex: 1,
  height: '44px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  ...(selected
    ? {
        background: '#4ab3bc',
        color: '#fff',
        border: 'none',
      }
    : {
        background: '#fff',
        color: '#737373',
        border: '1px solid #a6a6a6',
      }),
  '&:hover': {
    borderColor: '#4ab3bc',
  },
}));

// Time Setting (New Design)
export const TimeSettingRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const TimeInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '148px',
});

export const TimeSelectNew = styled(Select)({
  flex: 1,
  height: '44px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#a6a6a6',
  },
});

export const TimeColon = styled(Typography)({
  width: '12px',
  textAlign: 'center',
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const TimeTilde = styled(Typography)({
  width: '12px',
  textAlign: 'center',
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

// 금액설정 Card (Gray background input)
export const GrayInputWrapper = styled(Box)({
  background: '#f2f2f2',
  borderRadius: '4px',
  display: 'flex',
  flex: 1,
  '& .MuiOutlinedInput-root': {
    background: 'transparent',
  },
  '& .MuiTextField-root': {
    width: '100%',
  },
});

// 할인금액 with dropdown inside
export const DiscountUnitSelect = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  '& .unit-text': {
    fontSize: '13px',
    fontWeight: 500,
    color: '#737373',
  },
  '& .unit-arrow': {
    width: '14px',
    height: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#737373',
  },
});

// 기간설정 Card
export const DatePickerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const DatePickerField = styled(Box)({
  flex: 1,
  minWidth: 0,
  '& .MuiFormControl-root': {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    height: '44px',
    paddingRight: '8px',
  },
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    fontSize: '14px',
  },
});

export const DateSeparator = styled(Typography)({
  width: '11px',
  textAlign: 'center',
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  flexShrink: 0,
});

// CheckboxRow
export const CheckboxRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

// ============================================
// Step2 Card Layout
// ============================================

export const Step2CardsContainer = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-start',
  padding: '0 24px',
});

export const Step2Card = styled(Box)(({ width }) => ({
  background: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: width || 'auto',
  alignSelf: 'stretch',
}));

// 이용권 이미지
export const ImageUploadArea = styled(Box)({
  width: '100%',
  height: '200px',
  border: '1px solid #a6a6a6',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  cursor: 'pointer',
  background: '#fff',
  '&:hover': {
    borderColor: '#4ab3bc',
  },
});

export const ImageUploadIcon = styled(Box)({
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const ImageUploadText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#7dc8cf',
  textAlign: 'center',
});

export const SmallUploadButton = styled(Box)({
  width: '80px',
  height: '80px',
  border: '1px dashed #a6a6a6',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#4ab3bc',
  },
});

// 커리큘럼 등록
export const CurriculumCard = styled(Box)({
  background: '#f3f9fa',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const CurriculumHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const CurriculumDeleteIcon = styled(Box)({
  color: '#F87171',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
});

export const AddCurriculumButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  cursor: 'pointer',
  color: '#404040',
  width: '100%',
  '&:hover': {
    opacity: 0.7,
  },
});

// AI Toggle (New Design)
export const AIToggleBoxNew = styled(Box)({
  background: '#f3f9fa',
  borderRadius: '6px',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
});

export const AIToggleContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const AIIcon = styled(Box)({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#4ab3bc',
});

export const AIToggleLabelNew = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#4ab3bc',
});

export const StyledSwitch = styled(Switch)({
  width: 40,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4ab3bc',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: '#d9d9d9',
    opacity: 1,
  },
});

// 이용권 상세
export const RichTextContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: '300px',
  '& .ck-editor': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  '& .ck-editor__main': {
    flex: 1,
  },
  '& .ck-content': {
    minHeight: '240px',
  },
});

// Bottom Actions (Fixed)
export const BottomActionsNew = styled(Box)({
  position: 'fixed',
  bottom: '32px',
  right: '32px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  zIndex: 100,
});

export const CancelButtonNew = styled(Button)({
  width: '100px',
  height: '48px',
  background: '#a6a6a6',
  color: '#fff',
  fontSize: '20px',
  fontWeight: 600,
  borderRadius: '4px',
  '&:hover': {
    background: '#8f8f8f',
  },
});

export const PrevButtonNew = styled(Button)({
  width: '100px',
  height: '48px',
  background: '#fff',
  color: '#4ab3bc',
  fontSize: '20px',
  fontWeight: 600,
  borderRadius: '4px',
  border: '1px solid #4ab3bc',
  '&:hover': {
    background: '#f5f5f5',
  },
});

export const NextButtonNew = styled(Button)({
  width: '240px',
  height: '48px',
  background: '#4ab3bc',
  color: '#fff',
  fontSize: '20px',
  fontWeight: 600,
  borderRadius: '4px',
  '&:hover': {
    background: '#3d9a9f',
  },
  '&:disabled': {
    background: '#ccc',
  },
});

export const SaveButtonNew = styled(Button)({
  width: '240px',
  height: '48px',
  background: '#4ab3bc',
  color: '#fff',
  fontSize: '20px',
  fontWeight: 600,
  borderRadius: '4px',
  '&:hover': {
    background: '#3d9a9f',
  },
  '&:disabled': {
    background: '#ccc',
  },
});

// Toggle component
export const ToggleSwitch = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: '40px',
  height: '20px',
  borderRadius: '10px',
  background: checked ? '#4ab3bc' : '#d9d9d9',
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.2s',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: '#fff',
    top: '2px',
    left: checked ? '22px' : '2px',
    transition: 'left 0.2s',
  },
}));

// ============================================
// New Design - Cards Container (from TicketEditStyled)
// ============================================

const COLORS = {
  primary: '#4AB3BC',
  primaryDark: '#3E9FA7',
  primaryLight: '#7DC8CF',
  primaryBg: '#F3F9FA',
  white: '#FFFFFF',
  gray1: '#F2F2F2',
  gray2: '#D9D9D9',
  gray4: '#A6A6A6',
  gray6: '#737373',
  gray8: '#404040',
  gray9: '#262626',
  black: '#141414',
  error: '#DC2626',
};

export const CardsContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'flex-start',
});

// 기본정보 + 구독설정을 세로 한 칼럼으로 묶기 위한 wrapper.
// 다른 카드(이용설정/금액설정/기간설정)는 CardsContainer 의 가로 배치를 그대로 사용한다.
export const BasicInfoColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

// ============================================
// 이용기간 입력 콤보 (단일 박스 + borderless 단위 드롭다운)
// ============================================

export const UnitInputCombo = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError' && prop !== 'blind',
})(({ hasError, blind }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '44px',
  padding: '0 16px',
  border: `1px solid ${hasError ? '#dc2626' : '#A6A6A6'}`,
  borderRadius: '4px',
  background: '#fff',
  transition: 'border-color 0.15s ease',
  '&:focus-within': {
    borderColor: hasError ? '#dc2626' : '#4AB3BC',
  },
  // 구독제: 1개월 고정이라 입력 불가. 회색 블라인드 처리.
  ...(blind && {
    background: '#F5F5F5',
    border: '1px solid #E0E0E0',
    color: '#A6A6A6',
    pointerEvents: 'none',
  }),
}));

export const UnitInputField = styled('input')({
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  padding: 0,
  '&::placeholder': {
    color: '#A6A6A6',
    opacity: 1,
  },
  // 숫자 input의 spinner 제거
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '&[type=number]': {
    MozAppearance: 'textfield',
  },
});

export const UnitDropdownButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  '& svg': {
    color: '#0F7F85',
    fontSize: '16px',
  },
});

// 드롭다운이 아닌 고정 단위 텍스트 (예: "회"). UnitDropdownButton 의 비-인터랙티브 버전.
export const UnitFixedText = styled(Box)({
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
  whiteSpace: 'nowrap',
  userSelect: 'none',
});

export const InfoCard = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray2}`,
  borderRadius: '8px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  flex: 1,
  minWidth: 0,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
});

export const InfoCardHeaderRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  width: '100%',
  whiteSpace: 'nowrap',
});

export const InfoCardHint = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: COLORS.gray4,
});

export const FieldLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const FieldLabelWithInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const InputUnit = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.gray6,
  flexShrink: 0,
});

export const HalfWidthContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  width: '100%',
});

export const HalfWidthField = styled(Box)({
  flex: 1,
  minWidth: 0,
});

export const RadioGroupRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  height: '24px',
  width: '100%',
});

export const RadioOption = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
});

export const RadioCircleNew = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  border: checked ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.gray2}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: COLORS.white,
}));

export const RadioDotNew = styled(Box)({
  width: '12px',
  height: '12px',
  borderRadius: '6px',
  background: COLORS.primary,
});

export const RadioLabelNew = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.black,
});

export const TimePickerContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
});

export const TimePickerGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '148px',
});

export const TimeSelectSmall = styled(Select)({
  flex: 1,
  height: '44px',
  '& .MuiSelect-select': {
    padding: '12px',
    fontSize: '13px',
    color: COLORS.gray4,
  },
});

export const CheckboxNew = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '2px',
  border: `1.5px solid ${checked ? COLORS.primary : COLORS.gray2}`,
  background: checked ? COLORS.primary : COLORS.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

export const CheckboxLabelNew = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.black,
});

export const ToggleContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: COLORS.black,
});

export const InfoIconSmall = styled(Box)({
  width: '14px',
  height: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'help',
  color: COLORS.gray4,
});

// ============================================
// Sport Tag Input (Multi-select with tags)
// ============================================
export const SportTagInputContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  background: COLORS.white,
  border: `1px solid ${hasError ? '#dc2626' : COLORS.gray4}`,
  borderRadius: '4px',
  minHeight: '44px',
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  cursor: 'pointer',
  width: '100%',
  '&:hover': {
    borderColor: hasError ? '#dc2626' : COLORS.primary,
  },
  ...(hasError && {
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

export const SportTagsWrapper = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
  flex: 1,
});

export const SportTag = styled(Box)({
  background: COLORS.primary,
  borderRadius: '999px',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const SportTagText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.white,
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const SportTagCloseIcon = styled(Box)({
  width: '10px',
  height: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLORS.white,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
});

export const SportTagPlaceholder = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.gray4,
});

export const SportTagDropdownIcon = styled(Box)({
  width: '12px',
  height: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: COLORS.gray4,
});

// ============================================
// Step Indicator (Create Mode)
// ============================================
export const StepIndicatorContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: '16px',
});

export const StepTabsWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  paddingRight: '42px',
  isolation: 'isolate',
});

export const StepTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'zIndex',
})(({ active, zIndex }) => ({
  position: 'relative',
  display: 'inline-grid',
  gridTemplateRows: 'max-content',
  gridTemplateColumns: 'max-content',
  placeItems: 'start',
  marginRight: '-42px',
  zIndex: zIndex || 3,
}));

export const StepTabShapeFirst = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  gridRow: 1,
  gridColumn: 1,
  width: '160px',
  height: '48px',
  position: 'relative',
  background: active ? COLORS.primary : COLORS.gray1,
  clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)',
}));

export const StepTabShapeSecond = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  gridRow: 1,
  gridColumn: 1,
  width: '200px',
  height: '48px',
  position: 'relative',
  background: active ? COLORS.primary : COLORS.gray1,
  clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
}));

export const StepTabContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'isFirst',
})(({ active, isFirst }) => ({
  gridRow: 1,
  gridColumn: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginLeft: isFirst ? '23px' : '52px',
  marginTop: '10px',
  position: 'relative',
  zIndex: 1,
}));

export const StepTabNumber = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '15px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: active ? COLORS.white : COLORS.gray4,
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: active ? COLORS.primary : COLORS.white,
}));

export const StepTabLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: active ? COLORS.white : COLORS.gray6,
  whiteSpace: 'nowrap',
}));

export const BackToListLink = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  textDecoration: 'none',
});

export const BackToListIcon = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  background: COLORS.gray9,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const BackToListText = styled(Typography)({
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '20px',
  fontWeight: 500,
  color: COLORS.black,
  whiteSpace: 'nowrap',
});

// ============================================
// 복사 모드 배너 — 편집 화면과 구분되도록 복사 진행 중임을 알린다
// ============================================
export const CopyModeBanner = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  padding: '12px 16px',
  marginBottom: '16px',
  borderRadius: '6px',
  borderLeft: `4px solid ${COLORS.primary}`,
  background: COLORS.primaryBg,
});

export const CopyModeBannerTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: COLORS.primaryDark,
  whiteSpace: 'nowrap',
});

export const CopyModeBannerText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 400,
  color: COLORS.gray8,
});

// 생성 화면 우측 액션 묶음 — 불러오기 버튼과 목록으로 링크를 세로 중앙으로 나란히 둔다
export const HeaderActionGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const LoadProductButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  height: '36px',
  padding: '0 16px',
  background: '#FFFFFF',
  color: '#3E9FA7',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  border: '1px solid #4AB3BC',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    background: '#F3F9FA',
    boxShadow: 'none',
  },
});
